<template>
  <section id="stop_cancel" class="box_sso stop_cancel">
    <LogoutPendingModal
      :applied-at="cancelAppliedAt"
      :finished-at="cancelFinishedAt"
      :remaining-text="cancelRemainingText"
      :loading="request.intercept.submit"
      @restore="submit"
      @continue="backLogin" />
  </section>
</template>

<script lang="ts" type="text/tsx">
import {
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleFilled,
  FileDoneOutlined,
  HourglassOutlined
} from '@ant-design/icons-vue';
import { DataInterface } from '@interface/public';
import {
  RequestStopCancelUser, ResponseStopCancelUser, AxiosStopCancelUser
} from '@server/apiSSO';

const CancelProgressCard = {
  name: 'CancelProgressCard',
  components: {
    CalendarOutlined,
    ClockCircleOutlined,
    FileDoneOutlined,
    HourglassOutlined
  },
  props: {
    appliedAt: {
      type: String,
      default: ''
    },
    finishedAt: {
      type: String,
      default: ''
    },
    remainingText: {
      type: String,
      default: ''
    }
  },
  template: `
    <div class="cancel_progress_card">
      <div class="progress_title">
        <span class="progress_icon">
          <HourglassOutlined aria-hidden="true" focusable="false" />
        </span>
        <span>注销进度</span>
      </div>
      <div class="progress_item">
        <span class="progress_icon">
          <FileDoneOutlined aria-hidden="true" focusable="false" />
        </span>
        <span class="progress_label">申请时间</span>
        <span class="progress_value">{{ appliedAt || '--' }}</span>
      </div>
      <div class="progress_item">
        <span class="progress_icon">
          <CalendarOutlined aria-hidden="true" focusable="false" />
        </span>
        <span class="progress_label">预计完成时间</span>
        <span class="progress_value">{{ finishedAt || '--' }}</span>
      </div>
      <div class="progress_item">
        <span class="progress_icon">
          <ClockCircleOutlined aria-hidden="true" focusable="false" />
        </span>
        <span class="progress_label">剩余时间</span>
        <span class="progress_value progress_value_remain">{{ remainingText || '--' }}</span>
      </div>
    </div>
  `
};

const LogoutPendingModal = {
  name: 'LogoutPendingModal',
  components: {
    CancelProgressCard,
    CheckCircleFilled,
    ExclamationCircleFilled,
  },
  props: {
    appliedAt: {
      type: String,
      default: ''
    },
    finishedAt: {
      type: String,
      default: ''
    },
    remainingText: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: [ 'restore', 'continue' ],
  template: `
    <div class="logout_pending_modal">
      <div class="status_icon" aria-hidden="true">
        <ExclamationCircleFilled aria-hidden="true" focusable="false" />
      </div>
      <h1>账号正在注销中</h1>
      <p class="subtitle">
        您已提交注销申请，账号暂时无法登录<br />
        若改变主意，可在注销完成前随时撤销申请
      </p>
      <CancelProgressCard
        :applied-at="appliedAt"
        :finished-at="finishedAt"
        :remaining-text="remainingText" />
      <div class="benefit_card">
        <span class="benefit_icon">
          <CheckCircleFilled aria-hidden="true" focusable="false" />
        </span>
        <div class="benefit_content">
          <strong>注销前您可享有以下权益</strong>
          <span>撤销申请后，账号将恢复正常，历史数据不受影响</span>
        </div>
      </div>
      <a-button
        class="restore_button"
        type="primary"
        size="large"
        :loading="loading"
        block
        @click="$emit('restore')">
        恢复账号使用
      </a-button>
      <a-button
        class="continue_button"
        size="large"
        block
        @click="$emit('continue')">
        继续注销
      </a-button>
      <p class="helper_text">
        如有疑问，请发送邮件至
        <a href="mailto:service@galaxy-future.com">service@galaxy-future.com</a>
      </p>
    </div>
  `
};

export default {
  name: 'stopCancel',
  components: {
    LogoutPendingModal
  },
  computed: {
    cancelTimestamp(): string {
      const value = this.$route.query.cancelled_at;
      const routeTimestamp = Array.isArray(value) ? String(value[0] || '') : String(value || '');
      if (routeTimestamp) return routeTimestamp;
      
      return typeof sessionStorage === 'undefined'
        ? ''
        : sessionStorage.getItem('GFStopCancelCancelledAt') || '';
    },
    cancelAppliedAt(): string {
      return this.formatDate(this.cancelTimestamp);
    },
    cancelFinishedAt(): string {
      return this.cancelTimestamp
        ? this.formatDate(this.getExpireTimestamp(this.cancelTimestamp))
        : '';
    },
    cancelRemainingText(): string {
      if (!this.cancelTimestamp) return '';
      
      const start = this.getNormalizedTimestamp(this.cancelTimestamp);
      const end = this.getExpireTimestamp(this.cancelTimestamp);
      if (!start || !end) return '';
      
      return `${ Math.max(0, Math.ceil((end - start) / (24 * 60 * 60 * 1000))) }天`;
    }
  },
  data(): DataInterface {
    return {
      request: {
        intercept: {
          submit: false
        }
      }
    };
  },
  mounted(): void {
    if (!$RQ.SSO.check()) {
      this.$router.push($Enum.RouterEnum.SSOIndex);
      return;
    }
  },
  methods: {
    /**
     * 格式化时间
     * @param {string | number} value 时间戳
     * @return {string} 日期
     */
    formatDate(value: string | number): string {
      if (!value) return '';
      
      const timestamp = Number(value);
      if (Number.isNaN(timestamp)) return '';
      
      const normalized = this.getNormalizedTimestamp(value);
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = `${ date.getMonth() + 1 }`.padStart(2, '0');
      const day = `${ date.getDate() }`.padStart(2, '0');
      
      return `${ year }-${ month }-${ day }`;
    },
    
    /**
     * 计算过期时间戳
     * @param {string | number} value 时间戳
     * @return {number} 过期时间戳
     */
    getExpireTimestamp(value: string | number): number {
      const normalized = this.getNormalizedTimestamp(value);
      
      return normalized + 7 * 24 * 60 * 60 * 1000;
    },
    
    /**
     * 标准化时间戳
     * @param {string | number} value 时间戳
     * @return {number} 毫秒时间戳
     */
    getNormalizedTimestamp(value: string | number): number {
      const timestamp = Number(value);
      if (Number.isNaN(timestamp)) return 0;
      
      return `${ value }`.length > 10 ? timestamp : timestamp * 1000;
    },
    
    /**
     * 终止注销
     */
    async submit(): Promise<any> {
      if (this.request.intercept.submit) return;
      this.request.intercept.submit = true;
      
      await AxiosStopCancelUser({
        uid: $Store.User().userID
      } as RequestStopCancelUser)
        .then((response: any) => {
          const data: ResponseStopCancelUser = response.data;
          
          if (data.code !== $Enum.CodeEnum.Normal) return;
          
          $FN.Message('success', {
            content: '已终止注销'
          });
          typeof sessionStorage !== 'undefined' && sessionStorage.removeItem('GFStopCancelCancelledAt');
          $RQ.SSO.logout();
          this.$router.replace($Enum.RouterEnum.SSOIndex);
        })
        .finally(() => {
          this.request.intercept.submit = false;
        });
    },
    
    /**
     * 返回登录
     */
    backLogin(): void {
      typeof sessionStorage !== 'undefined' && sessionStorage.removeItem('GFStopCancelCancelledAt');
      $RQ.SSO.logout();
      this.$router.push($Enum.RouterEnum.SSOIndex);
    }
  }
};
</script>

<style lang="less" rel="stylesheet/less">
@import '@resource/css/_global.less';
@import '@resource/css/sso.less';

@image: '@{src_image}sso/';

.stop_cancel {
  overflow: auto;
  min-height: 100%;
  padding: 28px 16px;
  background:
    radial-gradient(circle at 50% 0, rgba(79, 139, 255, 0.16), transparent 34%),
    linear-gradient(180deg, #f3f8ff 0%, #eef5ff 48%, #f8fbff 100%);
  
  .logout_pending_modal {
    box-sizing: border-box;
    width: min(100%, 520px);
    padding: 42px 34px 30px;
    margin: 0 auto;
    border: 1px solid rgba(225, 235, 250, 0.88);
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 20px 60px rgba(40, 91, 150, 0.14);
    text-align: center;
  }
  
  .status_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 86px;
    height: 86px;
    margin: 0 auto 22px;
    border-radius: 50%;
    color: #ffffff;
    font-size: 40px;
    background:
      radial-gradient(circle, #7aa7ff 0%, #4f86f7 58%, #2f6ff0 100%);
    box-shadow:
      0 16px 30px rgba(55, 116, 239, 0.24),
      0 0 0 18px rgba(74, 133, 255, 0.10);
  }
  
  h1 {
    margin: 0;
    color: #1d2b46;
    font-size: 30px;
    font-weight: 650;
    line-height: 1.28;
    letter-spacing: 0.02em;
  }
  
  .subtitle {
    margin: 16px 0 28px;
    color: #53627a;
    font-size: 16px;
    line-height: 1.8;
  }
  
  .cancel_progress_card {
    padding: 22px 20px 8px;
    border: 1px solid #e6eefb;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(42, 87, 145, 0.10);
    text-align: left;
  }
  
  .progress_title {
    display: grid;
    grid-template-columns: 44px 1fr;
    align-items: center;
    padding-bottom: 18px;
    border-bottom: 1px solid #edf2fa;
    color: #1e2f4f;
    font-size: 18px;
    font-weight: 650;
    
    .progress_icon {
      color: #1f73ff;
      font-size: 18px;
    }
  }
  
  .progress_item {
    display: grid;
    grid-template-columns: 44px 1fr auto;
    align-items: center;
    min-height: 68px;
    
    & + .progress_item {
      border-top: 1px solid #edf2fa;
    }
  }
  
  .progress_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: start;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: #1f73ff;
    font-size: 18px;
    background: #eaf2ff;
  }
  
  .progress_label {
    color: #4b5b73;
    font-size: 15px;
    line-height: 1.4;
  }
  
  .progress_value {
    color: #25324a;
    font-size: 16px;
    font-weight: 520;
    font-variant-numeric: tabular-nums;
    line-height: 1.4;
  }
  
  .progress_value_remain {
    color: #ff6a00;
    font-weight: 650;
  }
  
  .benefit_card {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 18px 0 24px;
    padding: 16px 18px;
    border-radius: 16px;
    background: linear-gradient(90deg, #eaf4ff 0%, #f1f7ff 100%);
    text-align: left;
  }
  
  .benefit_icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 14px;
    color: #ffffff;
    font-size: 22px;
    background: linear-gradient(135deg, #2c7cff 0%, #1765f6 100%);
    box-shadow: 0 10px 20px rgba(31, 115, 255, 0.18);
  }
  
  .benefit_content {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    
    strong {
      color: #0c4cc7;
      font-size: 16px;
      font-weight: 650;
      line-height: 1.35;
    }
    
    span {
      color: #50627d;
      font-size: 14px;
      line-height: 1.55;
    }
  }
  
  .restore_button,
  .continue_button {
    height: 50px;
    border-radius: 12px;
    font-size: 17px;
    font-weight: 600;
  }
  
  .restore_button {
    border: 0;
    background: linear-gradient(135deg, #1d7dff 0%, #075fff 100%);
    box-shadow: 0 14px 24px rgba(15, 105, 255, 0.22);
    
    &:hover,
    &:focus {
      background: linear-gradient(135deg, #2a88ff 0%, #126aff 100%);
    }
  }
  
  .continue_button {
    margin-top: 14px;
    border: 1px solid #1770ff;
    color: #126cff;
    background: #ffffff;
    
    &:hover,
    &:focus {
      border-color: #126cff;
      color: #075fff;
      background: #f5f9ff;
    }
  }
  
  .helper_text {
    margin: 18px 0 0;
    color: #78869a;
    font-size: 14px;
    line-height: 1.6;
    
    a {
      color: #126cff;
      white-space: nowrap;
      word-break: break-all;
    }
  }
}

@media screen and (max-width: -1px+@grid_sm) {
  .stop_cancel {
    display: flex;
    align-items: center;
    min-height: 100%;
    padding: 18px 12px;
    
    .logout_pending_modal {
      padding: 32px 18px 24px;
      border-radius: 22px;
    }
    
    .status_icon {
      width: 72px;
      height: 72px;
      margin-bottom: 18px;
      font-size: 34px;
      box-shadow:
        0 12px 24px rgba(55, 116, 239, 0.22),
        0 0 0 14px rgba(74, 133, 255, 0.10);
    }
    
    h1 {
      font-size: 25px;
    }
    
    .subtitle {
      margin: 14px 0 22px;
      font-size: 15px;
      line-height: 1.75;
    }
    
    .cancel_progress_card {
      padding: 18px 16px 6px;
      border-radius: 16px;
    }
    
    .progress_item {
      grid-template-columns: 38px 1fr;
      gap: 0 8px;
      min-height: 64px;
    }
    
    .progress_title {
      grid-template-columns: 38px 1fr;
      gap: 0 8px;
    }
    
    .progress_value {
      grid-column: 2;
      margin-top: 3px;
      font-size: 15px;
    }
    
    .benefit_card {
      align-items: flex-start;
      padding: 15px;
    }
    
    .restore_button,
    .continue_button {
      height: 48px;
      font-size: 16px;
    }
    
    .helper_text {
      max-width: 260px;
      margin-right: auto;
      margin-left: auto;
      
      a {
        display: inline-block;
        white-space: normal;
      }
    }
  }
}
</style>
